import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  AppParametersService,
  AppState,
  BaseAppParameters,
  FilterState,
  RealEstateObject,
  RealEstateResponse,
} from '@immosky/types';
import { setRealEstateObjects } from '../../state/results/search-results/search-results.actions';
import { RealEstateSearchQueryBuilder } from './real-estate-search-query-builder';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  Actions,
  createEffect,
  ofType,
  ROOT_EFFECTS_INIT,
} from '@ngrx/effects';
import { resetMapRealEstateObjects } from '../../state/results/map-results/map-results.actions';
import { selectFilterState } from '@immosky/object-search-filter';
import { setIsLoadingSearchResults } from '@immosky/object-search-layout';
import { convertResponseToRealEstateObjects } from '@immosky/shared';

@Injectable({
  providedIn: 'root',
})
export class RealEstateSearchEffect {
  private filterState!: FilterState;

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      switchMap(() => {
        return this.store.pipe(
          select(selectFilterState),
          debounceTime(100),
          distinctUntilChanged(),
          switchMap((filterState: FilterState) => {
            this.filterState = filterState;
            const query = new RealEstateSearchQueryBuilder()
              .setFilterState(
                this.filterState,
                this.appParametersService.get().regionsIndex
              )
              .build();

            return this.search(query).pipe(
              tap(() => {
                this.store.dispatch(resetMapRealEstateObjects());
              }),
              switchMap((results: RealEstateObject[]) =>
                of(
                  setRealEstateObjects({
                    objects: results,
                  })
                )
              )
            );
          })
        );
      })
    )
  );

  constructor(
    private httpClient: HttpClient,
    private store: Store<AppState>,
    private actions$: Actions,
    private appParametersService: AppParametersService<BaseAppParameters>
  ) {}

  search(query: any): Observable<RealEstateObject[]> {
    this.setIsLoading(true);

    return this.httpClient
      .post<RealEstateResponse>(
        `${this.appParametersService.get().apiBaseUrl}/${
          this.appParametersService.get().propertiesIndex
        }/_search`,
        query
      )
      .pipe(
        map((response: RealEstateResponse) => {
          return convertResponseToRealEstateObjects(response);
        }),
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
        finalize(() => {
          this.setIsLoading(false);
        })
      );
  }

  private setIsLoading(isLoading: boolean) {
    this.store.dispatch(
      setIsLoadingSearchResults({
        isLoading,
      })
    );
  }
}
